<script setup lang="ts">
import { reactive, ref, onMounted } from 'vue';
import type { FormInstance, FormRules } from 'element-plus';
import { message as ElMessage } from '../../utils/message';
import api from '../../api';
import { useAuthStore } from '../../store/authStore';
import AppAlert from '../ui/AppAlert.vue';
import SettingsSidebar from '../settings/SettingsSidebar.vue';
import PhoneInput from '../ui/PhoneInput.vue';

const store       = useAuthStore() as any;
const loading     = ref(true);
const ruleFormRef = ref<FormInstance>();
const formError   = ref('');

const ruleForm = reactive({ name: '', email: '', phone_no: '' });

const rules = reactive<FormRules>({
  name:  [{ required: true, message: 'Name is required', trigger: 'blur' }],
  email: [{ required: true, message: 'Email is required', trigger: 'blur' }],
});

const loadProfile = async () => {
  try {
    const res = await api.get('customer/profile');
    const p   = res.data.data.profile;
    ruleForm.name     = p.name  || '';
    ruleForm.email    = p.email || '';
    ruleForm.phone_no = p.phone || '';
  } catch {
    ElMessage.error('Failed to load profile.');
  } finally {
    loading.value = false;
  }
};

const submitForm = async (formEl: FormInstance | undefined) => {
  if (!formEl) return;
  await formEl.validate(async (valid) => {
    if (!valid) return;
    formError.value = '';
    try {
      await api.patch('customer/profile', { name: ruleForm.name, phone: ruleForm.phone_no });
      if (store.user) (store.user as any).name = ruleForm.name;
      ElMessage.success('Profile updated');
    } catch (err: any) {
      formError.value = err.response?.data?.message || 'Failed to update profile.';
    }
  });
};

onMounted(loadProfile);
</script>

<template>
  <div class="st-page">

    <!-- Page header -->
    <div class="st-header">
      <div class="st-inner">
        <h1 class="st-title">Settings</h1>
        <p class="st-subtitle">Manage your account and business preferences</p>
      </div>
      <div class="st-header-rule"></div>
    </div>

    <!-- Body -->
    <div class="st-inner st-body">
      <SettingsSidebar />

      <div class="st-content">
        <div class="st-card" v-loading="loading">

          <!-- Hero -->
          <div class="st-hero">
            <div class="st-hero-avatar">{{ ruleForm.name?.charAt(0)?.toUpperCase() || '?' }}</div>
            <div class="st-hero-info">
              <div class="st-hero-name">{{ ruleForm.name || 'Your Name' }}</div>
              <div class="st-hero-email">{{ ruleForm.email || 'your@email.com' }}</div>
              <span class="st-hero-badge">Personal Account</span>
            </div>
          </div>

          <!-- Form -->
          <div class="st-form-body">
            <div class="st-section-label">Personal Information</div>
            <AppAlert :message="formError" />

            <el-form ref="ruleFormRef" :model="ruleForm" :rules="rules" label-width="0px" status-icon>
              <div class="st-grid">
                <div class="st-field">
                  <label class="st-label">Full Name <span class="st-req">*</span></label>
                  <el-form-item prop="name" class="st-fi">
                    <input v-model="ruleForm.name" class="st-input" placeholder="Your full name" />
                  </el-form-item>
                </div>
                <div class="st-field">
                  <label class="st-label">Email Address <span class="st-req">*</span></label>
                  <el-form-item prop="email" class="st-fi">
                    <input v-model="ruleForm.email" class="st-input" type="email" placeholder="your@email.com" readonly title="Your login email can't be changed here — contact support." style="background:#f3f4f6;color:#6b7280;cursor:not-allowed" />
                  </el-form-item>
                </div>
              </div>

              <div class="st-field">
                <label class="st-label">Phone Number</label>
                <PhoneInput v-model="ruleForm.phone_no" />
              </div>

              <div class="st-actions">
                <button class="st-btn" @click.prevent="submitForm(ruleFormRef)" :disabled="loading">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                  Save Changes
                </button>
              </div>
            </el-form>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
* { box-sizing: border-box; }

.st-page { font-family: 'DM Sans', sans-serif; background: white; min-height: 100%; }
.st-inner { max-width: 1280px; margin: 0 auto; padding: 0 32px; }

.st-header { background: white; padding: 24px 0 0; }
.st-header .st-inner { padding-bottom: 18px; }
.st-title { font-size: 1.4rem; font-weight: 700; color: #0f1729; margin: 0 0 3px; }
.st-subtitle { font-size: 0.83rem; color: #94a3b8; margin: 0; }
.st-header-rule { height: 1.5px; background: #e8f0ff; }

.st-body { display: flex; gap: 24px; padding-top: 28px; padding-bottom: 40px; align-items: flex-start; }
.st-content { flex: 1; min-width: 0; }

/* Card */
.st-card { background: white; border-radius: 14px; border: 1.5px solid #ccdeff; overflow: hidden; }

/* Hero */
.st-hero {
  background: linear-gradient(130deg, #0061eb 0%, #003a96 100%);
  padding: 28px 32px; display: flex; align-items: center; gap: 20px;
  position: relative; overflow: hidden;
}
.st-hero::before {
  content: ''; position: absolute; inset: 0;
  background: radial-gradient(circle at 80% 50%, rgba(255,255,255,0.07) 0%, transparent 60%);
}
.st-hero-avatar {
  width: 64px; height: 64px; border-radius: 50%; flex-shrink: 0;
  background: rgba(255,255,255,0.18); border: 2px solid rgba(255,255,255,0.3);
  color: white; display: flex; align-items: center; justify-content: center;
  font-size: 1.6rem; font-weight: 700; position: relative; z-index: 1;
}
.st-hero-info { position: relative; z-index: 1; }
.st-hero-name { font-size: 1.1rem; font-weight: 700; color: white; margin-bottom: 2px; }
.st-hero-email { font-size: 0.8rem; color: rgba(255,255,255,0.65); margin-bottom: 10px; }
.st-hero-badge {
  display: inline-block; background: rgba(255,255,255,0.15); border: 1px solid rgba(255,255,255,0.25);
  color: rgba(255,255,255,0.9); font-size: 0.65rem; font-weight: 700;
  letter-spacing: 0.07em; text-transform: uppercase; border-radius: 20px; padding: 3px 10px;
}

/* Form */
.st-form-body { padding: 28px 32px; }
.st-section-label {
  font-size: 0.67rem; font-weight: 700; color: #0061eb;
  text-transform: uppercase; letter-spacing: 0.08em;
  margin-bottom: 18px; padding-bottom: 8px; border-bottom: 1.5px solid #e8f0ff;
}
.st-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; margin-bottom: 16px; }
.st-field { margin-bottom: 16px; }
.st-label { display: block; font-size: 0.78rem; font-weight: 600; color: #475569; margin-bottom: 6px; }
.st-req { color: #dc2626; }
.st-fi { margin-bottom: 0 !important; }
:deep(.st-fi .el-form-item__content) { display: block; width: 100%; }
:deep(.st-fi .el-form-item__error) { padding-top: 4px; font-size: 0.74rem; }
.st-input {
  width: 100%; height: 42px; padding: 0 14px;
  border: 1.5px solid #e2e8f0; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.87rem; color: #1e293b;
  outline: none; transition: border-color 0.15s, box-shadow 0.15s; background: white;
}
.st-input:focus { border-color: #0061eb; box-shadow: 0 0 0 3px rgba(0,97,235,0.08); }
.st-input::placeholder { color: #94a3b8; }
.st-phone-row { display: flex; gap: 8px; }
.st-dial-select { flex: 0 0 110px; cursor: pointer; padding-right: 8px; }
.st-phone-num { flex: 1 1 auto; min-width: 0; }
.st-actions { display: flex; justify-content: flex-end; margin-top: 24px; padding-top: 18px; border-top: 1px solid #f1f5f9; }
.st-btn {
  height: 42px; padding: 0 28px;
  background: #0061eb; color: white; border: none; border-radius: 9px;
  font-family: 'DM Sans', sans-serif; font-size: 0.88rem; font-weight: 600;
  cursor: pointer; display: flex; align-items: center; gap: 8px; transition: background 0.15s;
}
.st-btn:hover:not(:disabled) { background: #0050c7; }
.st-btn:disabled { opacity: 0.5; cursor: not-allowed; }
</style>
